// Imports ------------------------------------------------------------------->
import {Service, service, inject} from "./di";
import {fetchJSON, fetchRejectJSON} from "../utils/fetch";
import {isCrawler} from "../utils/crawler";
import {ConfigService} from "./ConfigService";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->
export interface CityJSON {
	country: string;
	state: string;
	city: string;
	fallback_postal_code: string;
	geo_codes: string[];
}

@service("SeoService")
export class SeoService extends Service {
	@inject configService: ConfigService;
	private _cityCache: { [key: string]: CityJSON } = {};
	private _seoContentCache: { [key: string]: string } = {};
	private locales = [
		"en-ca",
		"fr-ca",
		"en-us",
	];

	private siteLocale: string = null;

	removeScripts(): void {
		if (!isCrawler()) {
			return;
		}

		const scripts = document.getElementsByTagName("script");
		let i = scripts.length;
		while (i--) {
			scripts[i].parentNode.removeChild(scripts[i]);
		}
	}

	setCrawlerCss(): void {
		if (!isCrawler()) {
			return;
		}

		const css = `
			.flipp-link {
				display: block !important;
			}
			li {
				margin-top: 20px !important;
				margin-bottom: 20px !important;
			}
			.flipp-link {
				margin-left: 20px !important;
			}
			.logo
			.myCustomElement {
				width: 50% !important;
			}
			img {
				margin-top: 20px !important;
				margin-bottom: 20px !important;
			}`;
		const head = document.head || document.getElementsByTagName("head")[0];
		const style = document.createElement("style");
		head.appendChild(style);
		style.type = "text/css";
		style.appendChild(document.createTextNode(css));
	}

	insertLoadingMarker(timeout: number = 0, markedId = "flipp-marked-element") {
		if (document.getElementById(markedId) !== null) {
			return;
		}

		const insertNode = () => {
			const node = document.createElement("div");
			node.id = markedId;
			node.style.display = "none";
			document.body.appendChild(node);
		};

		setTimeout(insertNode, timeout);
	}

	setDescription(textContent: string) {
		const metaDescription = document.querySelector("meta[name=description]");
		const metaOgDescription = document.querySelector("meta[name=og\\:description]");

		if (metaDescription)
			metaDescription.setAttribute("content", textContent);

		if (metaOgDescription)
			metaOgDescription.setAttribute("content", textContent);
	}

	// Some google links appear to badly treat our URLs and append a \
	// This appears usually at the end of a link and seems to infect our
	// postal_code and locale values occasionally.
	// This helper function slices this off if we find it.
	fixTrailingSlash(value: string): string {
		if (value && value.substr(-1) === "\\") {
			return value.substr(0, value.length - 1);
		}

		return value;
	}

	hasLocale() {
		return this.siteLocale !== null;
	}

	setLocale(value: string) {
		this.siteLocale = this.isLocale(value) ? value : "en-ca";
		document.documentElement.setAttribute("lang", this.siteLocale);
	}

	isLocale(value: string) {
		return this.locales.includes(value);
	}

	get locale() {
		return this.siteLocale;
	}

	get inCity() {
		return document.documentElement.getAttribute("in-city");
	}

	set inCity(value: string) {
		document.documentElement.setAttribute("in-city", value);
	}

	getCacheKey = (state: string, city: string) => `${city}_${state}`;

	setCityInfo(state: string, city: string, data: CityJSON) {
		const cacheKey = this.getCacheKey(state, city);
		if (!this._cityCache[cacheKey]) {
			this._cityCache[cacheKey] = data;
		}
	}

	async getCityInfo(state: string, city: string): Promise<CityJSON> {
		const cacheKey = this.getCacheKey(state, city);
		const cached: CityJSON = this._cityCache[cacheKey];
		if (cached) {
			return Promise.resolve(cached);
		}

		const cityParsed = city.split("_").map(c => c.toLowerCase()).join("_");
		const domain = this.configService.HOSTNAME === "localhost" ? "https://staging.flipp.com" : "";
		return fetchRejectJSON(`${domain}/data/cities/${state.toLowerCase()}_${cityParsed}.json`)
			.then((city: CityJSON) => {
				this._cityCache[cacheKey] = city;

				return Promise.resolve(city);
			});
	}

	async getSeoContent( store: string, locale: string, location: string): Promise<string> {
		const cacheKey = `${locale}_${location}_${store}`;
		const cached = this._seoContentCache[cacheKey];
		if (cached) {
			return Promise.resolve(cached);
		}
		let url = `${this.configService.NEXTJS_API}/seo-content/${locale}/${store}`;
		if (location) {
			url += `/${location}`;
		}

		return fetchJSON(url)
			.then((content: {text: string}) => {
				this._seoContentCache[cacheKey] = content.text;

				return Promise.resolve(content.text);
			}).catch(e => {
				return Promise.resolve("");
			});
	}

}
